// GuideArticle.js
// Template for a single guide. Reads :slug from the route, looks up metadata in
// guidesData, injects per-article SEO, and renders the matching article
// component from ARTICLE_COMPONENTS. Unknown/unpublished slug -> redirect to
// /guides.
//
// To add an article: import its component and add one line to ARTICLE_COMPONENTS
// keyed by slug (must match the guidesData slug and the route).

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import GuidesLayout from './GuidesLayout';
import { getGuideBySlug, colors } from './guidesData';
import { useGuideSeo } from './useGuideSeo';

// --- Article component registry ---
import HowToOrganizeWeeklyGroup from './articles/HowToOrganizeWeeklyGroup';
import HowToChoosePairings from './articles/HowToChoosePairings';
import EmailVsSoftware from './articles/EmailVsSoftware';
import CaptainsChoiceSetup from './articles/CaptainsChoiceSetup';
import BestBallSetup from './articles/BestBallSetup';
import GettingStarted from './articles/GettingStarted';
import ScoringYourRounds from './articles/ScoringYourRounds';
import PlayerGuide from './articles/PlayerGuide';
import StrokePlaySetup from './articles/StrokePlaySetup';
import ShambleSetup from './articles/ShambleSetup';
import SkinsSetup from './articles/SkinsSetup';
import NassauSetup from './articles/NassauSetup';
import MatchPlayGame from './articles/MatchPlayGame';
import StablefordGame from './articles/StablefordGame';
import SixesGame from './articles/SixesGame';
import ClosestToPinGame from './articles/ClosestToPinGame';
import DotsGame from './articles/DotsGame';
import PuttsGame from './articles/PuttsGame';
import StrokePlayGame from './articles/StrokePlayGame';
import BestBallGame from './articles/BestBallGame';
import TournamentSetup from './articles/TournamentSetup';

const ARTICLE_COMPONENTS = {
  'how-to-organize-a-weekly-golf-group': HowToOrganizeWeeklyGroup,
  'how-to-choose-golf-pairings': HowToChoosePairings,
  'organizing-golf-group-email-vs-software': EmailVsSoftware,
  'how-to-set-up-a-captains-choice-golf-tournament': CaptainsChoiceSetup,
  'how-to-set-up-a-best-ball-golf-tournament': BestBallSetup,
  'getting-started-with-tee-time-organizer': GettingStarted,
  'scoring-your-golf-rounds': ScoringYourRounds,
  'tee-time-organizer-player-guide': PlayerGuide,
  'how-to-set-up-a-stroke-play-golf-tournament': StrokePlaySetup,
  'how-to-set-up-a-shamble-golf-tournament': ShambleSetup,
  'how-to-set-up-a-skins-game': SkinsSetup,
  'how-to-set-up-a-nassau-golf-game': NassauSetup,
  'how-to-set-up-a-match-play-golf-game': MatchPlayGame,
  'how-to-set-up-a-stableford-golf-game': StablefordGame,
  'how-to-set-up-a-sixes-golf-game': SixesGame,
  'how-to-set-up-closest-to-the-pin': ClosestToPinGame,
  'how-to-set-up-a-dots-golf-game': DotsGame,
  'how-to-set-up-a-putts-golf-game': PuttsGame,
  'how-to-set-up-a-stroke-play-game': StrokePlayGame,
  'how-to-set-up-a-best-ball-game': BestBallGame,
  'how-to-run-a-golf-tournament-with-tee-time-organizer': TournamentSetup,
  // 'running-a-golf-league-first-time': RunningLeagueFirstTime,
  // 'ghin-handicaps-explained-for-leagues': GhinHandicapsExplained,
};

// "Close guide" bar (Sept 27, 2026). Guides opened from inside the app (links carry
// ?from=app) open in a new tab so the organizer can keep the guide beside his work;
// this bar closes that tab and returns him to TTO. Browsers only let a page close a
// tab it didn't navigate away from, so if the close is refused we say where to go.
// Public readers (no ?from=app) never see it. Hidden when printing.
// ?from=player (Sept 28, 2026): opened from the player app's 📖 Player Guide button.
// Same bar, but the fallback link goes back to the player app (/portal reads the
// player's saved link), not to the organizer dashboard.
function CloseGuideBar({ player = false }) {
  const [refused, setRefused] = useState(false);
  const close = () => {
    window.close();
    // Still here a moment later → the browser refused.
    setTimeout(() => setRefused(true), 300);
  };
  return (
    <div
      className="guide-no-print"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: colors.green50,
        border: `1px solid ${colors.green100}`,
        borderRadius: 10,
        padding: '10px 14px',
        margin: '0 0 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        fontSize: 14,
        color: colors.gray700,
      }}
    >
      <span>
        {refused
          ? 'Your browser kept this tab open. Switch back to the Tee Time Organizer tab, or '
          : 'This guide opened in its own tab so you can keep it beside Tee Time Organizer.'}
        {refused && (
          <a href={player ? '/portal' : '/dashboard'} style={{ color: colors.green700, fontWeight: 700 }}>
            {player ? 'go back to your app' : 'go to your dashboard'}
          </a>
        )}
        {refused && '.'}
      </span>
      <button
        type="button"
        onClick={close}
        style={{
          background: colors.green700,
          color: 'white',
          border: 'none',
          borderRadius: 8,
          padding: '8px 14px',
          fontSize: 14,
          fontWeight: 700,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        ✕ Close guide
      </button>
      <style>{'@media print { .guide-no-print { display: none !important; } }'}</style>
    </div>
  );
}

export default function GuideArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const from = new URLSearchParams(location.search).get('from');
  const fromApp = from === 'app' || from === 'player';
  const guide = getGuideBySlug(slug);
  const ArticleComponent = ARTICLE_COMPONENTS[slug];

  // SEO hook must run unconditionally (hooks rule); it no-ops on missing guide.
  useGuideSeo(guide && guide.published ? guide : null);

  // Redirect unknown or unpublished slugs back to the index.
  useEffect(() => {
    if (!guide || !guide.published || !ArticleComponent) {
      navigate('/guides', { replace: true });
    }
  }, [guide, ArticleComponent, navigate]);

  if (!guide || !guide.published || !ArticleComponent) {
    return null; // redirecting
  }

  return (
    <GuidesLayout maxWidth={760}>
      {/* Top back-navigation — gives readers an obvious way out without
          scrolling to the footer. Appears on every article automatically. */}
      {fromApp && <CloseGuideBar player={from === 'player'} />}
      <nav className="guide-no-print" style={{ display: 'flex', gap: 18, marginBottom: 24, fontSize: 14 }}>
        <span
          onClick={() => navigate('/guides')}
          style={{ color: colors.green700, fontWeight: 600, cursor: 'pointer' }}
        >
          ← All guides
        </span>
        <span
          onClick={() => navigate('/')}
          style={{ color: colors.gray500, fontWeight: 600, cursor: 'pointer' }}
        >
          Home
        </span>
      </nav>
      <ArticleComponent />
    </GuidesLayout>
  );
}
